import { format } from "date-fns";

/**
 * Tipos e resolução de `DateRangeFilter` — separados do componente
 * (`./DateRangeFilter.tsx`) só para o Fast Refresh não reclamar de um
 * arquivo que exporta componente e função ao mesmo tempo.
 */

export type DateRangePreset =
  | "ALL"
  | "TODAY"
  | "LAST_7"
  | "LAST_15"
  | "LAST_30"
  | "NEXT_7"
  | "NEXT_15"
  | "NEXT_30"
  | "THIS_MONTH"
  | "LAST_MONTH"
  | "THIS_YEAR"
  | "SPECIFIC"
  | "CUSTOM_RANGE";

export interface DateRangeValue {
  preset: DateRangePreset;
  /** ISO yyyy-mm-dd — só quando preset === 'SPECIFIC'. */
  date?: string;
  /** ISO yyyy-mm-dd — só quando preset === 'CUSTOM_RANGE' (início). */
  from?: string;
  /** ISO yyyy-mm-dd — só quando preset === 'CUSTOM_RANGE' (fim). */
  to?: string;
}

/**
 * Resolve um `DateRangeValue` em limites ISO yyyy-mm-dd (inclusive). Um dos
 * dois pode voltar indefinido quando o preset não limita aquele lado (ex.:
 * `NEXT_7` não tem `from` no passado).
 */
export function resolveDateRange(value: DateRangeValue): { from?: string; to?: string } {
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const paraIso = (data: Date) => format(data, "yyyy-MM-dd");

  switch (value.preset) {
    case "TODAY":
      return { from: paraIso(hoje), to: paraIso(hoje) };
    case "LAST_7": {
      const inicio = new Date(hoje);
      inicio.setDate(inicio.getDate() - 6);
      return { from: paraIso(inicio), to: paraIso(hoje) };
    }
    case "LAST_15": {
      const inicio = new Date(hoje);
      inicio.setDate(inicio.getDate() - 14);
      return { from: paraIso(inicio), to: paraIso(hoje) };
    }
    case "LAST_30": {
      const inicio = new Date(hoje);
      inicio.setDate(inicio.getDate() - 29);
      return { from: paraIso(inicio), to: paraIso(hoje) };
    }
    case "NEXT_7": {
      const fim = new Date(hoje);
      fim.setDate(fim.getDate() + 7);
      return { from: paraIso(hoje), to: paraIso(fim) };
    }
    case "NEXT_15": {
      const fim = new Date(hoje);
      fim.setDate(fim.getDate() + 15);
      return { from: paraIso(hoje), to: paraIso(fim) };
    }
    case "NEXT_30": {
      const fim = new Date(hoje);
      fim.setDate(fim.getDate() + 30);
      return { from: paraIso(hoje), to: paraIso(fim) };
    }
    case "THIS_MONTH": {
      const inicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      const fim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
      return { from: paraIso(inicio), to: paraIso(fim) };
    }
    case "LAST_MONTH": {
      const inicio = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
      const fim = new Date(hoje.getFullYear(), hoje.getMonth(), 0);
      return { from: paraIso(inicio), to: paraIso(fim) };
    }
    case "THIS_YEAR": {
      const inicio = new Date(hoje.getFullYear(), 0, 1);
      const fim = new Date(hoje.getFullYear(), 11, 31);
      return { from: paraIso(inicio), to: paraIso(fim) };
    }
    case "SPECIFIC":
      return value.date ? { from: value.date, to: value.date } : {};
    case "CUSTOM_RANGE":
      return { from: value.from, to: value.to };
    case "ALL":
    default:
      return {};
  }
}
